import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Clock, MapPin, Users, CheckCircle2, Star, ShieldCheck, Lock,
  X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, PawPrint, Camera, Printer, KeyRound,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (v) => Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
const fmtShort = (d) => new Date(d).toLocaleDateString('it-IT');

function Countdown({ expiresAt }) {
  const [left, setLeft] = useState('');
  useEffect(() => {
    const tick = () => {
      const ms = new Date(expiresAt) - new Date();
      if (ms <= 0) return setLeft('scaduto');
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
      setLeft(`${h}h ${m}m`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [expiresAt]);
  return <span data-testid="quote-countdown">{left}</span>;
}

function Lightbox({ photos, start, onClose }) {
  const [idx, setIdx] = useState(start);
  const prev = useCallback((e) => { e && e.stopPropagation(); setIdx(i => (i - 1 + photos.length) % photos.length); }, [photos.length]);
  const next = useCallback((e) => { e && e.stopPropagation(); setIdx(i => (i + 1) % photos.length); }, [photos.length]);
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', h); document.body.style.overflow = ''; };
  }, [next, prev, onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={onClose} data-testid="quote-lightbox">
      <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10" onClick={onClose} data-testid="lightbox-close"><X className="h-8 w-8" /></button>
      <button className="absolute left-1 sm:left-6 text-white/80 hover:text-white p-2 z-10" onClick={prev} data-testid="lightbox-prev"><ChevronLeft className="h-10 w-10" /></button>
      <img src={photos[idx]} alt="" className="max-h-[85vh] max-w-[88vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
      <button className="absolute right-1 sm:right-6 text-white/80 hover:text-white p-2 z-10" onClick={next} data-testid="lightbox-next"><ChevronRight className="h-10 w-10" /></button>
      <p className="absolute bottom-4 text-white/70 text-sm" data-testid="lightbox-counter">{idx + 1} / {photos.length}</p>
    </div>
  );
}

function PropertyDetails({ snap, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const hasContent = snap.description_html || (snap.services || []).length > 0 || snap.position;
  if (!hasContent) return null;
  return (
    <div className="mt-3 border-t pt-3">
      <button onClick={() => setOpen(o => !o)}
        className="text-sm font-semibold text-blue-700 hover:text-blue-800 flex items-center gap-1"
        data-testid={`quote-details-toggle-${snap.property_code}`}>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {open ? 'Nascondi dettagli' : 'Scopri di più: descrizione, posizione e servizi'}
      </button>
      {open && (
        <div className="mt-3 space-y-4 text-sm" data-testid={`quote-details-${snap.property_code}`}>
          {snap.position && (
            <p className="flex items-start gap-1.5 text-slate-600">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" /> {snap.position}
            </p>
          )}
          {snap.description_html && (
            <div className="text-slate-600 leading-relaxed [&_p]:mb-2 [&_br]:block"
              dangerouslySetInnerHTML={{ __html: snap.description_html }} />
          )}
          {(snap.rule_children || snap.rule_pets) && (
            <div className="text-xs text-slate-500 space-y-1 bg-slate-50 rounded-lg p-3">
              {snap.rule_children && <p>👶 {snap.rule_children}</p>}
              {snap.rule_pets && <p>🐾 {snap.rule_pets}</p>}
            </div>
          )}
          {(snap.services || []).length > 0 && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Servizi</p>
              <div className="flex flex-wrap gap-1.5">
                {snap.services.map(s => (
                  <span key={s} className="bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded-full">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlanSummary({ tariff, opts }) {
  const p = tariff === 'r' ? opts.r : opts.nr;
  if (tariff === 'r') return (
    <p className="text-sm text-slate-600" data-testid="quote-plan-summary">
      <b>0 € oggi.</b> Salviamo in modo sicuro il tuo metodo di pagamento e l'intero importo di <b>{eur(p.total)}</b> verrà
      addebitato automaticamente il <b>{fmtShort(p.later_date)}</b> (7 giorni prima dell'arrivo). Cancellazione gratuita fino ad allora.
    </p>
  );
  if (p.later_amount > 0) return (
    <p className="text-sm text-slate-600" data-testid="quote-plan-summary">
      Oggi paghi l'acconto di <b>{eur(p.pay_now)}</b> (30%). Il saldo di <b>{eur(p.later_amount)}</b> scade
      il <b>{fmtShort(p.later_date)}</b> (30 giorni prima dell'arrivo): scegli qui sotto se pagarlo
      in unica soluzione o in 3 rate con PayPal.
    </p>
  );
  return (
    <p className="text-sm text-slate-600" data-testid="quote-plan-summary">
      Oggi paghi l'intero importo: <b>{eur(p.total)}</b>.
    </p>
  );
}

function PaymentBox({ snap, token, onFallbackRequest, requesting }) {
  const opts = snap.payment_options;
  const [tariff, setTariff] = useState('nr');
  const [balanceMode, setBalanceMode] = useState('auto');
  const [payMethod, setPayMethod] = useState('paypal');
  const [consent, setConsent] = useState(false);
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState(null);
  if (!opts) return null;
  const code = snap.property_code;
  const showBalanceChoice = tariff === 'nr' && opts.nr.later_amount > 0;

  const pay = async () => {
    setErr(null);
    if (!consent) { setErr('Per procedere devi autorizzare il trattamento dei dati di pagamento.'); return; }
    setPaying(true);
    try {
      const res = await axios.post(`${API}/api/public/quote/${token}/checkout`, {
        property_id: snap.property_id, tariff, consent: true,
        balance_mode: showBalanceChoice ? balanceMode : 'auto',
        payment_method: payMethod,
      });
      window.location.href = res.data.approve_url;
    } catch (e) {
      setErr(e.response?.data?.detail || 'Errore, riprova o contattaci');
      setPaying(false);
    }
  };

  return (
    <div className="mt-4 border rounded-xl p-4 bg-slate-50 space-y-3" data-testid={`quote-payment-box-${code}`}>
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Scegli la tariffa e prenota subito</p>
      <div className="space-y-2">
        <label className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer bg-white ${tariff === 'nr' ? 'border-blue-600 ring-1 ring-blue-600' : 'border-slate-200'}`}>
          <input type="radio" name={`tariff-${code}`} checked={tariff === 'nr'} onChange={() => setTariff('nr')} className="mt-1" data-testid={`quote-tariff-nr-${code}`} />
          <span className="flex-1 text-sm">
            <span className="flex justify-between font-semibold"><span>Non rimborsabile</span><span className="text-blue-700">{eur(opts.nr.total)}</span></span>
            <span className="text-xs text-slate-500">Il miglior prezzo — non cancellabile</span>
          </span>
        </label>
        {opts.r.available && (
          <label className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer bg-white ${tariff === 'r' ? 'border-blue-600 ring-1 ring-blue-600' : 'border-slate-200'}`}>
            <input type="radio" name={`tariff-${code}`} checked={tariff === 'r'} onChange={() => setTariff('r')} className="mt-1" data-testid={`quote-tariff-r-${code}`} />
            <span className="flex-1 text-sm">
              <span className="flex justify-between font-semibold"><span>Rimborsabile</span><span className="text-blue-700">{eur(opts.r.total)}</span></span>
              <span className="text-xs text-slate-500">0 € oggi · cancellazione gratuita fino a 7 giorni dall'arrivo</span>
            </span>
          </label>
        )}
      </div>
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
        <PlanSummary tariff={tariff} opts={opts} />
      </div>
      {showBalanceChoice && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Come vuoi pagare il saldo?</p>
          <label className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer bg-white ${balanceMode === 'auto' ? 'border-blue-600 ring-1 ring-blue-600' : 'border-slate-200'}`}>
            <input type="radio" name={`balmode-${code}`} checked={balanceMode === 'auto'} onChange={() => setBalanceMode('auto')} className="mt-1" data-testid={`quote-balance-mode-auto-${code}`} />
            <span className="flex-1 text-sm">
              <span className="font-semibold">Unica soluzione</span>
              <span className="block text-xs text-slate-500 mt-0.5">Addebito automatico di {eur(opts.nr.later_amount)} il {fmtShort(opts.nr.later_date)} sul metodo salvato — zero pensieri</span>
            </span>
          </label>
          <label className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer bg-white ${balanceMode === 'payin3' ? 'border-blue-600 ring-1 ring-blue-600' : 'border-slate-200'}`}>
            <input type="radio" name={`balmode-${code}`} checked={balanceMode === 'payin3'} onChange={() => setBalanceMode('payin3')} className="mt-1" data-testid={`quote-balance-mode-payin3-${code}`} />
            <span className="flex-1 text-sm">
              <span className="font-semibold">In 3 rate con PayPal</span>
              <span className="block text-xs text-slate-500 mt-0.5">Un giorno prima della scadenza ricevi l'email con il link: paghi su PayPal scegliendo <b>"Paga in 3 rate" senza interessi</b>. Se non fai nulla, addebito automatico dell'intero saldo in unica soluzione.</span>
            </span>
          </label>
        </div>
      )}
      <div className="space-y-2">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Come vuoi pagare?</p>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setPayMethod('paypal')}
            className={`p-2.5 rounded-lg border text-sm font-semibold bg-white transition-colors ${payMethod === 'paypal' ? 'border-blue-600 ring-1 ring-blue-600 text-blue-700' : 'border-slate-200 text-slate-600'}`}
            data-testid={`quote-method-paypal-${code}`}>
            <span className="block">PayPal</span>
            <span className="block text-[10px] font-normal text-slate-400">con il tuo conto PayPal</span>
          </button>
          <button type="button" onClick={() => setPayMethod('card')}
            className={`p-2.5 rounded-lg border text-sm font-semibold bg-white transition-colors ${payMethod === 'card' ? 'border-blue-600 ring-1 ring-blue-600 text-blue-700' : 'border-slate-200 text-slate-600'}`}
            data-testid={`quote-method-card-${code}`}>
            <span className="block">💳 Carta</span>
            <span className="block text-[10px] font-normal text-slate-400">Visa, Mastercard, Amex — senza account</span>
          </button>
        </div>
      </div>
      <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid={`quote-consent-${code}`} />
        <span>Autorizzo Holiday World al trattamento dei miei dati di pagamento e al salvataggio sicuro del metodo di pagamento (conto PayPal o carta) per gli addebiti previsti dal piano indicato sopra, ai sensi del GDPR.</span>
      </label>
      {err && <p className="text-xs text-red-600" data-testid={`quote-pay-error-${code}`}>{err}</p>}
      <button onClick={pay} disabled={paying}
        className="w-full bg-[#0070ba] hover:bg-[#005ea6] disabled:opacity-50 text-white font-bold py-3.5 rounded-full transition-colors flex items-center justify-center gap-2"
        data-testid={`quote-pay-btn-${code}`}>
        {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
        {paying ? 'Ti portiamo al pagamento sicuro...' : (tariff === 'r' ? 'Conferma prenotazione (0 € oggi)' : `Paga ${eur(opts.nr.pay_now)} ${payMethod === 'card' ? 'con carta' : 'con PayPal'}`)}
      </button>
      <p className="text-center text-[11px] text-slate-400 flex items-center justify-center gap-1">
        <ShieldCheck className="h-3.5 w-3.5" /> Pagamento sicuro con PayPal — carta o conto PayPal
      </p>
      <p className="text-center text-[11px] text-blue-700 bg-blue-50 rounded-lg py-1.5 px-2" data-testid={`quote-payin3-${code}`}>
        💳 Su PayPal puoi scegliere anche <b>"Paga in 3 rate" senza interessi</b> (clienti idonei, importi 30–2.000 €)
      </p>
      <button onClick={() => onFallbackRequest(snap.property_id)} disabled={!!requesting}
        className="w-full text-center text-xs text-slate-500 underline hover:text-slate-700"
        data-testid={`public-quote-book-${code}`}>
        Preferisci essere ricontattato dall'ufficio? Invia una richiesta
      </button>
    </div>
  );
}

function BalanceBox({ token, balance }) {
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState(null);
  const payBalance = async () => {
    setErr(null);
    setPaying(true);
    try {
      const res = await axios.post(`${API}/api/public/quote/${token}/balance-checkout`);
      window.location.href = res.data.approve_url;
    } catch (e) {
      setErr(e.response?.data?.detail || 'Errore, riprova o contattaci');
      setPaying(false);
    }
  };
  return (
    <div className="bg-white border border-blue-200 rounded-xl p-4 text-sm space-y-2.5 no-print" data-testid="quote-balance-box">
      <p className="font-bold text-blue-700">💶 Saldo di {eur(balance.amount)} — {balance.mode === 'payin3' ? `scadenza ${fmtShort(balance.due_date)}` : `addebito automatico il ${fmtShort(balance.due_date)}`}</p>
      <p className="text-slate-600">
        {balance.mode === 'payin3' ? (
          <>Hai scelto il <b>saldo in 3 rate con PayPal</b>: un giorno prima della scadenza ti invieremo una email con il link per pagare.
            Puoi pagare anche subito da qui — su PayPal potrai scegliere <b>"Paga in 3 rate" senza interessi</b> (clienti idonei, importi 30–2.000 €).
            Se non fai nulla, alla scadenza addebiteremo l'intero saldo in un'unica soluzione sul metodo salvato.</>
        ) : (
          <>Preferisci saldare adesso? Puoi pagare il saldo quando vuoi con PayPal:
            <b> disponibile anche il "Paga in 3 rate" senza interessi</b> (clienti idonei, importi 30–2.000 €).
            Se non fai nulla, l'addebito automatico avverrà come previsto alla data indicata.</>
        )}
      </p>
      {err && <p className="text-xs text-red-600" data-testid="quote-balance-error">{err}</p>}
      <button onClick={payBalance} disabled={paying}
        className="w-full bg-[#0070ba] hover:bg-[#005ea6] disabled:opacity-50 text-white font-bold py-3 rounded-full transition-colors flex items-center justify-center gap-2"
        data-testid="quote-balance-pay-btn">
        {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
        {paying ? 'Ti portiamo su PayPal...' : `Paga il saldo di ${eur(balance.amount)} con PayPal`}
      </button>
    </div>
  );
}

export default function QuotePublicPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [requesting, setRequesting] = useState(null);
  const [requested, setRequested] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [payResult, setPayResult] = useState(null);
  const [balanceResult, setBalanceResult] = useState(null);
  const [payError, setPayError] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { photos, start }

  const fetchQuote = useCallback(() => axios.get(`${API}/api/public/quote/${token}`)
    .then(res => {
      setData(res.data);
      if (res.data.requested_property_id) setRequested(res.data.requested_property_id);
    })
    .catch(err => setError(err.response?.status === 404 ? 'Preventivo non trovato' : 'Errore di caricamento')), [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pp = params.get('pp'), pid = params.get('pid');
    const cleanUrl = () => window.history.replaceState({}, '', window.location.pathname);
    if ((pp === 'order' || pp === 'vault') && pid) {
      setCompleting(true);
      axios.post(`${API}/api/public/quote/${token}/checkout/${pid}/complete`)
        .then(res => setPayResult(res.data))
        .catch(err => setPayError(err.response?.data?.detail || 'Pagamento non riuscito: riprova o contattaci'))
        .finally(() => { cleanUrl(); setCompleting(false); fetchQuote(); });
    } else if (pp === 'balance' && pid) {
      setCompleting(true);
      axios.post(`${API}/api/public/quote/${token}/balance-checkout/${pid}/complete`)
        .then(res => setBalanceResult(res.data))
        .catch(err => setPayError(err.response?.data?.detail || 'Pagamento del saldo non riuscito: riprova o contattaci'))
        .finally(() => { cleanUrl(); setCompleting(false); fetchQuote(); });
    } else {
      if (pp === 'cancel') { setPayError('Pagamento annullato. Puoi riprovare quando vuoi.'); cleanUrl(); }
      fetchQuote();
    }
  }, [token, fetchQuote]);

  const requestBooking = async (pid) => {
    setRequesting(pid);
    try {
      const res = await axios.post(`${API}/api/public/quote/${token}/request`, { property_id: pid });
      setRequested(pid);
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore, riprova o contattaci');
    } finally { setRequesting(null); }
  };

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <p className="text-slate-500">{error}</p>
    </div>
  );
  if (completing) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center gap-3" data-testid="quote-completing">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      <p className="text-slate-600 text-sm font-medium">Confermiamo il tuo pagamento, un attimo...</p>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );

  const expired = data.status === 'expired';
  const payment = data.payment || (payResult?.booking_code ? {
    property_id: null, booking_code: payResult.booking_code, tariff: payResult.tariff,
    paid_now: payResult.paid_now, later_amount: payResult.later_amount, later_date: payResult.later_date,
  } : null);
  const nights = Math.round((new Date(data.check_out) - new Date(data.check_in)) / 86400000);
  const kids = data.children_under_10 || 0;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="public-quote-page">
      {lightbox && <Lightbox photos={lightbox.photos} start={lightbox.start} onClose={() => setLightbox(null)} />}
      <div className="bg-blue-700 text-white">
        <div className="max-w-3xl mx-auto px-5 py-8 text-center">
          <img src="/hw-logo-blu.png" alt="Holiday World" className="h-12 sm:h-14 bg-white rounded-xl p-1.5 mb-4 mx-auto" data-testid="quote-logo" />
          <p className="text-xs uppercase tracking-widest opacity-70 mb-1">Holiday World · Preventivo personalizzato</p>
          <h1 className="text-2xl sm:text-3xl font-bold">Ciao {data.client_first_name}, ecco le case per te!</h1>
          <div className="flex flex-wrap justify-center gap-4 mt-3 text-sm opacity-95">
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {fmtDate(data.check_in)} → {fmtDate(data.check_out)} · {nights} notti</span>
            <span className="flex items-center gap-1.5" data-testid="quote-guests-header">
              <Users className="h-4 w-4" /> {data.guests} ospiti{kids > 0 && ` (di cui ${kids} ${kids === 1 ? 'bimbo' : 'bimbi'} sotto i 10 anni — soggiorno gratis)`}
            </span>
            {data.pets && <span className="flex items-center gap-1.5" data-testid="quote-pets-header"><PawPrint className="h-4 w-4" /> Con animale</span>}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-6 space-y-5">
        {payment && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-5 text-emerald-800 text-sm space-y-1 print-clean" data-testid="quote-paid-banner">
            <div className="flex items-start justify-between gap-3">
              <p className="font-bold text-base flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> Prenotazione confermata! 🎉</p>
              <button onClick={() => window.print()}
                className="no-print flex items-center gap-1.5 bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold px-3 py-1.5 rounded-full"
                data-testid="quote-print-btn">
                <Printer className="h-3.5 w-3.5" /> Stampa
              </button>
            </div>
            {payment.booking_code && <p>Codice prenotazione: <b>{payment.booking_code}</b></p>}
            {payment.paid_now > 0 && <p>Pagato oggi: <b>{eur(payment.paid_now)}</b></p>}
            {payment.later_amount > 0 && (data.balance?.status === 'done'
              ? <p data-testid="quote-balance-done-line">Saldo di <b>{eur(payment.later_amount)}</b> pagato ✓ — prenotazione completamente saldata.</p>
              : <p>{payment.paid_now > 0 ? 'Saldo' : 'Importo'} di <b>{eur(payment.later_amount)}</b> addebitato automaticamente il <b>{fmtShort(payment.later_date)}</b>.</p>)}
            <p className="text-xs text-emerald-600">Ti abbiamo inviato una email di conferma con tutti i dettagli.</p>
          </div>
        )}
        {balanceResult && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-4 text-emerald-800 text-sm font-semibold flex items-center gap-2 print-clean" data-testid="quote-balance-paid-banner">
            <CheckCircle2 className="h-5 w-5 shrink-0" /> Saldo di {eur(balanceResult.amount)} pagato! La tua prenotazione è completamente saldata. 🎉
          </div>
        )}
        {payment && data.balance?.can_pay_now && !balanceResult && (
          <BalanceBox token={token} balance={data.balance} />
        )}
        {payment && (
          <div className="bg-white border border-blue-200 rounded-xl p-4 text-sm print-clean" data-testid="quote-checkin-box">
            <p className="font-bold text-blue-700 flex items-center gap-2 mb-1.5"><KeyRound className="h-4 w-4" /> Il tuo check-in</p>
            {data.checkin_contact ? (
              <p className="text-slate-600">
                Al tuo arrivo ti accoglierà <b>{data.checkin_contact.name || 'il nostro incaricato'}</b><br />
                {data.checkin_contact.phone && <>📞 <a href={`tel:${data.checkin_contact.phone.replace(/\s/g, '')}`} className="text-blue-700 underline">{data.checkin_contact.phone}</a><br /></>}
                {data.checkin_contact.email && <>✉️ <a href={`mailto:${data.checkin_contact.email}`} className="text-blue-700 underline">{data.checkin_contact.email}</a></>}
              </p>
            ) : (
              <p className="text-slate-600">Per organizzare il check-in contatta il nostro ufficio: 📞 <a href="tel:+390240030785" className="text-blue-700 underline">02 4003 0785</a> · ✉️ {data.office_email}</p>
            )}
          </div>
        )}
        {payError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm flex justify-between items-start gap-3" data-testid="quote-pay-error-banner">
            <span>⚠️ {payError}</span>
            <button onClick={() => setPayError(null)} className="text-xs underline shrink-0">chiudi</button>
          </div>
        )}
        {!payment && (expired ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700 text-sm" data-testid="quote-expired-banner">
            ⏳ <b>Questo preventivo è scaduto.</b> Gli immobili sono tornati disponibili alla vendita: scrivici a <a href={`mailto:${data.office_email}`} className="underline">{data.office_email}</a> per una nuova quotazione.
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-amber-800 text-sm flex items-center gap-2" data-testid="quote-validity-banner">
            <Clock className="h-4 w-4 shrink-0" />
            <span>Preventivo valido ancora <b><Countdown expiresAt={data.expires_at} /></b> — dopo, gli immobili torneranno disponibili alla vendita.</span>
          </div>
        ))}

        {(payment && payment.property_id
          ? data.snapshots.filter(s => s.property_id === payment.property_id)
          : data.snapshots).map(s => {
          const per = s.fees?.periodic;
          const isReq = requested === s.property_id;
          const isPaid = payment && (payment.property_id === s.property_id || (!payment.property_id && payResult));
          const photos = (s.gallery && s.gallery.length ? s.gallery : (s.cover_url ? [s.cover_url] : []));
          return (
            <div key={s.property_id} className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`public-quote-card-${s.property_code}`}>
              {s.cover_url && (
                <div className="relative cursor-zoom-in group" onClick={() => setLightbox({ photos, start: 0 })} data-testid={`quote-cover-${s.property_code}`}>
                  <img src={s.cover_url} alt={s.name} className="w-full h-56 sm:h-72 object-cover" />
                  {photos.length > 1 && (
                    <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 group-hover:bg-black/80 transition-colors">
                      <Camera className="h-3.5 w-3.5" /> {photos.length} foto
                    </span>
                  )}
                </div>
              )}
              <div className="p-5 sm:p-6">
                {s.is_top && <span className="inline-flex items-center gap-1 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full mb-2"><Star className="h-3 w-3" /> IL PIÙ RICHIESTO</span>}
                <h2 className="text-xl font-bold text-slate-800">{s.name}</h2>
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3.5 w-3.5" /> {s.position || `${s.city || ''} ${s.province ? `(${s.province})` : ''}`}</p>
                {photos.length > 1 && (
                  <div className="flex gap-1.5 mt-3 overflow-x-auto pb-1">
                    {photos.slice(1, 6).map((g, i) => (
                      <img key={i} src={g} alt="" onClick={() => setLightbox({ photos, start: i + 1 })}
                        className="h-16 w-24 object-cover rounded-lg shrink-0 cursor-zoom-in hover:opacity-80 transition-opacity"
                        data-testid={`quote-thumb-${s.property_code}-${i}`} />
                    ))}
                    {photos.length > 6 && (
                      <button onClick={() => setLightbox({ photos, start: 6 })}
                        className="h-16 w-24 shrink-0 rounded-lg bg-slate-800/80 text-white text-xs font-semibold flex items-center justify-center hover:bg-slate-800"
                        data-testid={`quote-more-photos-${s.property_code}`}>
                        +{photos.length - 6} foto
                      </button>
                    )}
                  </div>
                )}
                <PropertyDetails snap={s} defaultOpen={!!isPaid} />
                {(() => { const m = s.accommodation.months; const amt = (v) => m ? `${eur(Math.round((v / m) * 100) / 100)}/mese` : eur(v); return (
                <div className="mt-4 border-t pt-3 text-sm space-y-1.5">
                  <div className="flex justify-between"><span className="text-slate-500">Soggiorno {s.accommodation.nights} notti{s.accommodation.submultiple_persons ? ` (prezzo per ${s.accommodation.submultiple_persons} ${s.accommodation.submultiple_persons === 1 ? 'persona' : 'persone'})` : ''}{s.accommodation.pricing_rule === 'monthly_discount_20' ? ' (sconto mensile −20%)' : s.accommodation.pricing_rule === 'mid_term' ? ' (tariffa medio termine)' : ''}</span><span>{amt(s.accommodation.accommodation_total)}</span></div>
                  {s.fees.cleaning > 0 && <div className="flex justify-between"><span className="text-slate-500">Pulizia finale</span><span>{amt(s.fees.cleaning)}</span></div>}
                  {s.fees.linen > 0 && <div className="flex justify-between"><span className="text-slate-500">Biancheria ({s.fees.linen_detail})</span><span>{amt(s.fees.linen)}</span></div>}
                  {per && <div className="flex justify-between"><span className="text-slate-500">Pulizia {per.label} + cambio biancheria ({per.interventions}×{eur(per.unit_cost)})</span><span>{amt(per.total)}</span></div>}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>{m ? 'Prezzo mensile (tutto incluso)' : 'Totale'}</span><span className="text-blue-700" data-testid={`public-quote-total-${s.property_code}`}>{m ? `${eur(s.monthly_total)}/mese` : eur(s.total)}</span></div>
                  {m && <p className="text-[11px] text-slate-400" data-testid={`public-quote-monthly-note-${s.property_code}`}>Soggiorno di {s.accommodation.nights} notti (≈ {m} mesi) — il totale complessivo ti viene mostrato al momento del pagamento</p>}
                  {s.tourist_tax && (
                    <p className="text-xs text-amber-700 pt-1" data-testid={`public-quote-tax-${s.property_code}`}>
                      ℹ️ Tassa di soggiorno (Comune di {s.tourist_tax.comune}): <b>{eur(s.tourist_tax.amount)}</b> — da pagare in loco, non inclusa nel totale
                    </p>
                  )}
                </div>
                ); })()}
                {isPaid ? (
                  <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 text-emerald-700 text-sm flex items-center gap-2" data-testid={`public-quote-paid-${s.property_code}`}>
                    <CheckCircle2 className="h-5 w-5 shrink-0" /> Hai prenotato questa casa. Ti aspettiamo!
                  </div>
                ) : payment ? null : !expired && (
                  isReq ? (
                    <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 text-emerald-700 text-sm flex items-center gap-2" data-testid={`public-quote-requested-${s.property_code}`}>
                      <CheckCircle2 className="h-5 w-5 shrink-0" /> Richiesta inviata! Ti contatteremo a breve per confermare la prenotazione.
                    </div>
                  ) : (
                    <PaymentBox snap={s} token={token} onFallbackRequest={requestBooking} requesting={requesting} />
                  )
                )}
              </div>
            </div>
          );
        })}

        <p className="text-center text-xs text-slate-400 py-4">
          Domande? Scrivici a <a href={`mailto:${data.office_email}`} className="underline">{data.office_email}</a> · Holiday World
        </p>
      </div>
    </div>
  );
}
